/**
 * Modal Component - Sistema di overlay robusto
 * Supporta diverse dimensioni, template predefiniti e fallback automatico
 */

import React, { useEffect, useRef } from 'react';
import { useModal } from '../../contexts/ModalContext';

const Modal = ({ id, component: Component, props, options }) => {
  const { closeModal } = useModal();
  const modalRef = useRef(null);

  const {
    size = 'medium',
    closable = true,
    closeOnOverlayClick = true,
    showCloseButton = true,
  } = options;

  // Chiudi modale con ESC
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && closable) {
        closeModal(id);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [closable, closeModal, id]);

  // Invia altezza reale al parent iframe per auto-resize senza scroll interno
  useEffect(() => {
    if (!modalRef.current) return;

    let lastHeight = 0;

    const sendHeight = () => {
      const box = modalRef.current;
      if (!box) return;
      // Wrapper: p-4 (16px top+bottom) + my-8 (32px top+bottom) = 96px totali verticali
      const totalHeight = box.scrollHeight + 96;
      window.parent.postMessage({ type: 'anutel-resize', height: totalHeight }, '*');
      // Al primo render (modale appena aperto) o se il contenuto si accorcia significativamente,
      // notifica il parent di riposizionare lo scroll in cima all'iframe
      if (lastHeight === 0 || lastHeight - totalHeight > 50) {
        window.parent.postMessage({ type: 'anutel-modal-open' }, '*');
      }
      lastHeight = totalHeight;
    };

    const ro = new ResizeObserver(sendHeight);
    ro.observe(modalRef.current);
    sendHeight();

    return () => ro.disconnect();
  }, []);

  const handleOverlayClick = (e) => {
    if (closeOnOverlayClick && closable && e.target === e.currentTarget) {
      closeModal(id);
    }
  };

  const handleClose = () => {
    if (closable) {
      closeModal(id);
    }
  };

  // Dimensioni del modale
  const sizeClasses = {
    small: 'max-w-md',
    medium: 'max-w-6xl',  // ✅ FASE 4: Aumentato da max-w-2xl per modali più larghi
    large: 'max-w-7xl',
    fullscreen: 'max-w-full h-full m-0',
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black bg-opacity-50 overflow-y-auto animate-fadeIn"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
    >
      <div className="min-h-screen flex items-start justify-center p-4">
        <div
          ref={modalRef}
          className={`relative bg-white rounded-lg shadow-2xl w-full ${sizeClasses[size]} my-8 animate-slideUp`}
        >
          {/* Contenuto del modale */}
          <Component {...props} closeModal={() => closeModal(id)} />
        </div>
      </div>
    </div>
  );
};

/**
 * ModalRenderer - Renderizza tutti i modali aperti
 */
export const ModalRenderer = () => {
  const { modals } = useModal();

  if (modals.length === 0) return null;

  return (
    <div className="modal-container">
      {modals.map((modal) => (
        <Modal key={modal.id} {...modal} />
      ))}
    </div>
  );
};

export default Modal;
